import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CloseCombined } from "../../assets/CloseCombined";
import { LogoCombined } from "../../assets/LogoCombined";
import { MenuCombined } from "../../assets/MenuCombined";
import { PATHS } from "../../routes/paths";
import api from "../../services/api";
import BrandFlower from "../../assets/BrandFlower";
import {
  getCidadesAtivasIniciais,
  listCidadesAtivas,
  subscribeCidadeUpdates,
} from "../../services/cidades";
import "./style.css";

const NAV_LINKS = [
  { label: "QUEM SOMOS", to: PATHS.quemSomos },
  { label: "METODOLOGIA", to: PATHS.metodologia },
  { label: "ONDE ESTAMOS", to: PATHS.ondeEstamos, dynamicDropdown: true },
  { label: "PARA EMPRESAS", to: PATHS.paraEmpresas },
  { label: "BLOG", to: PATHS.blog },
  { label: "TRANSPARÊNCIA", to: PATHS.transparencia },
  { label: "CONTATO", to: PATHS.contato },
  { label: "APOIE", to: PATHS.apoie },
];

const SOCIAL_LINKS = [
  {
    name: "LinkedIn",
    href: "https://linkedin.com",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="white"
        aria-hidden="true"
      >
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
        <rect x="2" y="9" width="4" height="12" />
        <circle cx="4" cy="4" r="2" />
      </svg>
    ),
  },
  {
    name: "Instagram",
    href: "https://instagram.com",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    ),
  },
  {
    name: "YouTube",
    href: "https://youtube.com",
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
        <polygon
          points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"
          fill="white"
          stroke="none"
        />
      </svg>
    ),
  },
];

function OverlayDecorations() {
  return (
    <div className="header-overlay-decorations" aria-hidden="true">
      <BrandFlower
        petalColor="#004998"
        dropColor="#004998"
        preserveAspectRatio="none"
      />
    </div>
  );
}

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [cidades, setCidades] = useState(getCidadesAtivasIniciais);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadCities = () =>
      listCidadesAtivas()
        .then((data) => {
          if (Array.isArray(data) && data.length > 0) {
            setCidades(data);
          }
        })
        .catch(() => undefined);

    loadCities();
    return subscribeCidadeUpdates(loadCities);
  }, []);

  useEffect(() => {
    api.get("/auth/verify")
      .then(() => setIsAuthenticated(true))
      .catch(() => setIsAuthenticated(false));
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY <= 12);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        handleCloseMenu();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const handleNavigate = (path) => {
    setDropdownOpen(false);
    setMenuOpen(false);
    navigate(path);
    window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 0);
  };

  const handleOpenMenu = () => {
    setDropdownOpen(false);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setDropdownOpen(false);
    setMenuOpen(false);
  };

  const handleLogoClick = () => {
    handleCloseMenu();
    navigate(PATHS.home);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const dropdownExtraHeight = dropdownOpen ? cidades.length * 36 + 16 : 0;

  return (
    <>
      <button
        className={`header-logo ${!isAtTop ? "is-hidden-on-scroll" : ""}`}
        onClick={handleLogoClick}
        aria-label="Ir para a página inicial"
      >
        <LogoCombined />
      </button>

      {!menuOpen && (
        <button
          className={`header-menu-button ${
            !isAtTop ? "is-hidden-on-scroll" : ""
          }`}
          onClick={handleOpenMenu}
          aria-label="Abrir menu"
        >
          <MenuCombined />
        </button>
      )}

      <div
        className={`header-overlay ${menuOpen ? "is-open" : ""} ${
          dropdownOpen ? "has-dropdown" : ""
        }`}
        style={{ "--header-dropdown-extra": `${dropdownExtraHeight}px` }}
        aria-hidden={!menuOpen}
      >
        <OverlayDecorations />

        <div className="header-overlay-top">
          <button
            className="header-overlay-logo"
            onClick={handleLogoClick}
            aria-label="Ir para a página inicial"
          >
            <LogoCombined />
          </button>

          <button
            className="header-close-button"
            onClick={handleCloseMenu}
            aria-label="Fechar menu"
          >
            <CloseCombined />
          </button>
        </div>

        <div className="header-overlay-content">
          <div className="header-menu-column">
            <nav className="header-nav" aria-label="Menu principal">
            {NAV_LINKS.map((item) => (
              <div key={item.label} className="header-nav-group">
                <button
                  type="button"
                  className="header-nav-link"
                  onClick={() => {
                    if (item.dynamicDropdown) {
                      setDropdownOpen((current) => !current);
                      return;
                    }

                    handleNavigate(item.to);
                  }}
                  aria-expanded={item.dynamicDropdown ? dropdownOpen : undefined}
                  aria-controls={item.dynamicDropdown ? "header-submenu" : undefined}
                >
                  <span>{item.label}</span>

                  {item.dynamicDropdown && (
                    <span
                      className={`header-chevron ${dropdownOpen ? "is-open" : ""}`}
                      aria-hidden="true"
                    />
                  )}
                </button>

                {item.dynamicDropdown && dropdownOpen && (
                  <div id="header-submenu" className="header-submenu">
                    {cidades.length === 0 ? (
                      <span className="header-submenu-empty">
                        Nenhuma cidade cadastrada
                      </span>
                    ) : (
                      cidades.map((cidade) => (
                        <button
                          key={cidade._id || cidade.slug}
                          type="button"
                          className="header-submenu-link"
                          onClick={() =>
                            handleNavigate(`${PATHS.ondeEstamos}/${cidade.slug}`)
                          }
                        >
                          {cidade.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
            </nav>

            {isAuthenticated && (
              <button
                type="button"
                className="header-admin-link"
                onClick={() => handleNavigate(PATHS.admin)}
              >
                ÁREA ADMINISTRATIVA
              </button>
            )}

            <div className="header-socials">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.name}
                  className="header-social-link"
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
